"use client";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";

import { Layout } from "@portal/app/_components/ui-elements/Layout";
import { getUrls } from "@portal/app/_common/lib/urls";
import { Dreamcrumbs } from "@portal/app/_components/ui-elements/Dreamcrumbs";
import { BlogPost } from "./_components/BlogPost";
import { DreamSearchResultMarkdownDescription } from "@portal/app/health/_components/components/DreamSearchResult/DreamSearchResultPrimitives";
import { BlogHomePageProps } from "@portal/app/blog/page";

export const BlogHomePage = ({ posts, hasNextPage, nextPage }: BlogHomePageProps) => {
  const urls = getUrls();
  const featuredPosts = posts.slice(0, 3);
  const allPosts = posts.slice(3);

  return (
    <Layout footer="BOTH">
      <Container>
        <Dreamcrumbs links={[{ title: "Home", path: urls.Home() }]} currentTitle="Dreambound Blog" />
        <Content>
          <Title variant="page_title" sx={{ textTransform: "uppercase" }}>
            Dreambound Blog
          </Title>
          <Subtitle>Stories, news & advice from the good people at Dreambound</Subtitle>
          <Section>
            <PostsGrid data-testid="blog-page-featured-posts">
              {featuredPosts.map((post) => (
                <BlogPost key={post.id} blogPost={post} />
              ))}
            </PostsGrid>
          </Section>
          <Section>
            <SectionHeading variant="new_h2">All posts</SectionHeading>
            <PostsGrid data-testid="blog-page-all-posts">
              {allPosts.map((post) => (
                <BlogPost key={post.id} blogPost={post} />
              ))}
            </PostsGrid>
          </Section>
        </Content>
        {hasNextPage && (
          <SeeMoreButtonWrapper>
            <SeeMoreButton
              href={`${urls.BlogHome()}?page=${nextPage}`}
              variant="contained"
              data-testid="blog-page-see-more-button"
            >
              See more
            </SeeMoreButton>
          </SeeMoreButtonWrapper>
        )}
      </Container>
    </Layout>
  );
};

const Container = styled(Box)`
  padding: var(--horizontal-padding);
`;

const Content = styled(Box)`
  max-width: ${({ theme }) => `${theme.breakpoints.values.lg}px`};
  margin: var(--horizontal-padding) auto;
`;

const Title = styled(Typography)`
  font-size: 3rem;
`;

const Subtitle = styled(Typography)`
  margin-bottom: 4rem;
`;

// Section-to-section spacing lives here; heading-to-grid spacing on SectionHeading.
const Section = styled(Box)`
  & + & {
    margin-top: 4rem;
  }
`;

const SectionHeading = styled(Typography)`
  margin: 0 0 1.5rem;
`;

// Row gap is larger than column gap so rows read as rows rather than the
// cards running together vertically. Two columns start at md, not sm: a
// ~250px card at sm would truncate most titles under the two-line clamp.
const PostsGrid = styled(Box)`
  display: grid;
  grid-template-columns: 1fr;
  row-gap: 2.5rem;

  ${({ theme }) => theme.breakpoints.up("md")} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: 2rem;
    row-gap: 3rem;
  }

  ${({ theme }) => theme.breakpoints.up("lg")} {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

export const BlogMarkdownDescription = styled(DreamSearchResultMarkdownDescription)`
  * {
    font-size: 0.85rem;
  }
`;

const SeeMoreButtonWrapper = styled(Box)`
  display: flex;
  justify-content: center;
  margin: 0 auto 2rem;
`;

const SeeMoreButton = styled(Button)`
  min-width: 10rem;
`;
